import { useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, PointerEvent, MouseEvent } from 'react'

import { clsx } from 'clsx'

import {
  ChannelIcon,
  ChevronDownIcon,
  Collapsible,
  Flex,
  Popover,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { getChannelName } from '~publish/helpers/channels'

import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import {
  SyncPopoverContent,
  SyncSection,
  SyncSectionText,
} from '~publish/components/SyncPopover'
import {
  CommentsSyncSectionIcon,
  LikesSyncSection,
  MentionsSyncSection,
  SyncTriggerIcon,
} from '~publish/pages/Community/components/SyncPopover'
import type {
  Service,
  ChannelType,
} from '~publish/pages/Community/graphqlEnums'
import { getIsRealtimeSync } from '~publish/pages/Community/util/channelSyncHelpers'

import syncPopoverStyles from '~publish/components/SyncPopover/syncPopover.module.css'
import styles from './AllChannelsSyncActions.module.css'

type SyncChannel = {
  id: string
  service: Service
  type: ChannelType
}

/** Distinct services, in the order their first channel appears. */
function uniqueServices(channels: SyncChannel[]): Service[] {
  return Array.from(new Set(channels.map((channel) => channel.service)))
}

/** Matches the single-channel HoverCard's "fast" delays, so both controls feel the same. */
const HOVER_DELAY_MS = 150

/** Touch and pen have no hover; a tap goes through the click path instead. */
const hasHover = (event: PointerEvent<HTMLElement>): boolean =>
  event.pointerType !== 'touch' && event.pointerType !== 'pen'

/**
 * Open state for a Popover that a mouse can also open by resting on the
 * trigger. The popover body holds a button, so it stays a Popover rather
 * than a HoverCard: a click, a keypress or a tap opens it the usual way,
 * moving focus inside and keeping it open until dismissed. A hover opens it
 * without touching focus and closes it once the pointer has left both the
 * trigger and the content; clicking the trigger while hovered pins it open.
 */
function useHoverOpenPopover(): {
  open: boolean
  onOpenChange: (open: boolean) => void
  triggerProps: {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => void
    onPointerLeave: (event: PointerEvent<HTMLElement>) => void
    onClick: (event: MouseEvent<HTMLElement>) => void
  }
  contentProps: {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => void
    onPointerLeave: (event: PointerEvent<HTMLElement>) => void
    onOpenAutoFocus: (event: Event) => void
    onCloseAutoFocus: (event: Event) => void
  }
} {
  const [open, setOpen] = useState(false)
  /** Whether the popover is currently open because of a hover, not a click. */
  const hoverOpenRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => clearTimer, [])

  const onPointerEnter = (event: PointerEvent<HTMLElement>): void => {
    if (!hasHover(event)) return
    clearTimer()
    if (open) return
    timerRef.current = setTimeout(() => {
      hoverOpenRef.current = true
      setOpen(true)
    }, HOVER_DELAY_MS)
  }

  const onPointerLeave = (event: PointerEvent<HTMLElement>): void => {
    if (!hasHover(event)) return
    clearTimer()
    if (!hoverOpenRef.current) return
    timerRef.current = setTimeout(() => setOpen(false), HOVER_DELAY_MS)
  }

  return {
    open,
    onOpenChange: (nextOpen: boolean): void => {
      clearTimer()
      setOpen(nextOpen)
    },
    triggerProps: {
      onPointerEnter,
      onPointerLeave,
      onClick: (event): void => {
        clearTimer()
        if (open && hoverOpenRef.current) {
          // Pin a hover-opened popover instead of letting Radix toggle it shut.
          hoverOpenRef.current = false
          event.preventDefault()
          return
        }
        hoverOpenRef.current = false
      },
    },
    contentProps: {
      onPointerEnter: (event): void => {
        if (!hasHover(event)) return
        clearTimer()
      },
      onPointerLeave,
      onOpenAutoFocus: (event): void => {
        if (hoverOpenRef.current) event.preventDefault()
      },
      onCloseAutoFocus: (event): void => {
        // Focus never entered a hover-opened popover, so there is nothing
        // to hand back to the trigger.
        if (hoverOpenRef.current) event.preventDefault()
        hoverOpenRef.current = false
      },
    },
  }
}

/**
 * A cadence and the networks it applies to, rendered as an inline run of
 * service icons so the sentence wraps to the full popover width.
 *
 * The icons carry the only indication of which networks a cadence covers, so
 * the same information is spelled out for screen readers ahead of them rather
 * than announced as a row of unlabelled images.
 */
const ServiceCadenceRow = ({
  services,
  description,
}: {
  services: Service[]
  description: string
}): JSX.Element => (
  <SyncSectionText>
    <VisuallyHidden>{services.map(getChannelName).join(', ')}: </VisuallyHidden>
    <span className={styles.serviceIcons} aria-hidden="true">
      {services.map((service) => (
        // ChannelIcon defaults to a white glyph for sitting on a branded
        // avatar; these sit inline on the popover, so they take the
        // surrounding text colour instead.
        <ChannelIcon key={service} type={service} size="small" color="subtle" />
      ))}
    </span>
    {description}
  </SyncSectionText>
)

/**
 * Comments section for a mix of realtime and polled channels. Collapsed it only
 * admits that cadence varies; expanded it names which networks do what. There
 * is no manual sync here because a backfill is scoped to one channel.
 */
const MixedCommentsSection = ({
  realtimeServices,
  polledServices,
}: {
  realtimeServices: Service[]
  polledServices: Service[]
}): JSX.Element => {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SyncSection
        icon={<CommentsSyncSectionIcon size="small" />}
        label="Comments & replies"
        trailing={
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className={styles.expandButton}
              aria-label={
                open
                  ? 'Hide how each channel updates'
                  : 'Show how each channel updates'
              }
            >
              <ChevronDownIcon
                size="small"
                className={clsx(
                  styles.expandIcon,
                  open && styles.expandIconOpen,
                )}
              />
            </button>
          </Collapsible.Trigger>
        }
      >
        {/* The summary and the detail rows occupy the same spot: the summary
            fades away while the details settle into its place from above. */}
        <div className={styles.cadence}>
          <div
            className={styles.summary}
            data-testid="sync-cadence-summary"
            data-visible={!open}
            aria-hidden={open}
          >
            <SyncSectionText>Varies depending on the channel</SyncSectionText>
          </div>
          <Collapsible.Content className={styles.details}>
            <Flex
              direction="column"
              gap="2xs"
              fullWidth
              className={styles.detailsInner}
            >
              <ServiceCadenceRow
                services={realtimeServices}
                description="Updated every 2 min"
              />
              <ServiceCadenceRow
                services={polledServices}
                description="Updated periodically. Go to single channel for more details."
              />
            </Flex>
          </Collapsible.Content>
        </div>
      </SyncSection>
    </Collapsible>
  )
}

/**
 * Sync status for the all-channels Community header. Unlike the single-channel
 * control this never offers a manual sync and never turns green unless every
 * channel receives engagement on its own.
 */
export const AllChannelsSyncActions = ({
  channels,
}: {
  channels: SyncChannel[]
}): JSX.Element | null => {
  const { config } = useCommunityConfig()
  const popover = useHoverOpenPopover()

  const { realtimeServices, polledServices } = useMemo(() => {
    const realtime = channels.filter((channel) =>
      getIsRealtimeSync(channel, config),
    )
    const polled = channels.filter(
      (channel) => !getIsRealtimeSync(channel, config),
    )
    return {
      realtimeServices: uniqueServices(realtime),
      polledServices: uniqueServices(polled),
    }
  }, [channels, config])

  if (channels.length === 0) return null

  const allRealtime = polledServices.length === 0
  const allPolled = realtimeServices.length === 0

  return (
    <Popover open={popover.open} onOpenChange={popover.onOpenChange}>
      <Popover.Trigger>
        <button
          type="button"
          className={syncPopoverStyles.trigger}
          aria-label="About sync status"
          {...popover.triggerProps}
        >
          <SyncTriggerIcon state={allRealtime ? 'upToDate' : 'synced'} />
        </button>
      </Popover.Trigger>
      <Popover.Content
        aria-label="Sync status"
        className={syncPopoverStyles.content}
        align="center"
        side="bottom"
        arrow
        {...popover.contentProps}
      >
        <SyncPopoverContent title="How your channels stay updated">
          {allRealtime || allPolled ? (
            <SyncSection
              icon={<CommentsSyncSectionIcon size="small" />}
              label="Comments & replies"
            >
              <SyncSectionText>
                {allRealtime
                  ? 'Updated every 2 min'
                  : 'Updated periodically. Interval depends on the channel'}
              </SyncSectionText>
            </SyncSection>
          ) : (
            <MixedCommentsSection
              realtimeServices={realtimeServices}
              polledServices={polledServices}
            />
          )}
          {config.hasSupportedMentionsChannel(channels) && (
            <MentionsSyncSection />
          )}
          <LikesSyncSection />
        </SyncPopoverContent>
      </Popover.Content>
    </Popover>
  )
}
